/**
 * The marks the player draws for objects people added that are not followed yet, or could not be: one per `add`
 * still `following` or `failed`, on its frame. An object added by a click is marked where the person clicked, never
 * at the box the server guessed around it (that guess can be most of the picture); one added by a box, at its box.
 * `offset` moves the edit's frame into the video shown (the live clip starts later); `followingLabel` is what a
 * mark still being followed says.
 */
export const pendingAddMarks = (edits, { offset = 0, followingLabel = 'following…' } = {}) =>
	(edits || [])
		.filter(e => e.type === 'add' && (e.status === 'following' || e.status === 'failed'))
		.map(e => {
			// an edit from before the tool was kept: a point means a click. A click with no point to mark keeps
			// its box rather than vanishing
			const byClick = e.tool ? e.tool === 'click' : !!e.point;
			return {
				frame: e.frame + offset,
				box: byClick && e.point ? null : e.box,
				point: e.point,
				status: e.status,
				label: e.status === 'following' ? followingLabel : null,
			};
		});

/**
 * Where a mark's label goes, as [x, y] of its left baseline: beside the mark on the right when that fits in the
 * view, else on the left, else squeezed into the view over the mark; null when the mark itself is outside the view
 * (zoomed in and panned away), so no label is left floating at an edge with nothing it belongs to. `mark` is the
 * mark's [x0, y0, x1, y1], `y` the baseline beside it and `width` the text's width, all in view px; `W` and `H`
 * are the view, `gap` the space between the mark and its text, `pad` the least distance from the view's edge
 * and `ascent` the text's height above its baseline, so the top of the text stays inside the view too.
 */
export const labelPlace = ({ mark, y, width, W, H, gap, pad, ascent }) => {
	const [x0, y0, x1, y1] = mark;
	if (x1 < 0 || y1 < 0 || x0 > W || y0 > H) return null;
	let x = x1 + gap;
	if (x + width > W - pad) x = x0 - gap - width;
	if (x < pad) x = Math.max(pad, Math.min(x1 + gap, W - pad - width));
	return [x, Math.max(pad + ascent, Math.min(y, H - pad))];
};
